/**
 * Map-anchored status badge for missions in pending_approval / scheduled
 * lifecycle states. Shows a small drone icon at the bound dock's home with
 * a flashing "Pending operator approval" tag (pending) or a live "Starts
 * in MM:SS" countdown (scheduled).
 *
 * Implementation: one mapboxgl.Marker per active mission, with a custom
 * HTML element. The countdown is updated locally via setInterval on the
 * element's textContent — re-rendering the whole React tree every second
 * would force re-creation of the marker each tick, so we keep the marker
 * stable and mutate the inner span imperatively.
 *
 * The badge hides for in_flight (live view drone takes over) and completed
 * (mission done), and for any mission whose dock is missing.
 */

import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import type { DemoMissionFlowState } from './types';
import './DockMissionStatusBadge.css';

interface ActiveBadge {
  marker: mapboxgl.Marker;
  countdownEl: HTMLElement | null;
  intervalId: number | null;
}

interface BadgeData {
  missionId: string;
  missionName: string;
  state: 'pending_approval' | 'scheduled';
  lng: number;
  lat: number;
  scheduledStartAt: number | undefined;
}

/** Format a positive ms duration as M:SS (clamped at 0:00). */
function formatCountdown(remainingMs: number): string {
  const safe = Math.max(0, remainingMs);
  const totalSec = Math.ceil(safe / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/** Build the badge HTML for a given state. */
function buildBadgeElement(b: BadgeData): { root: HTMLElement; countdownEl: HTMLElement | null } {
  const root = document.createElement('div');
  root.className = 'dock-status-badge';
  root.setAttribute('data-state', b.state);

  // Drone icon — top-down quadcopter SVG, kept simple. Same colour family
  // as the live-view drone so the visual association holds when the badge
  // hands off to the live view at countdown end.
  const icon = document.createElement('div');
  icon.className = 'dock-status-badge__icon';
  icon.innerHTML = `
    <svg viewBox="0 0 32 32" width="22" height="22" aria-hidden="true">
      <circle cx="16" cy="16" r="3.5" fill="#22d3ee" stroke="#fff" stroke-width="1.2"/>
      <line x1="16" y1="16" x2="6" y2="6"  stroke="#22d3ee" stroke-width="1.6"/>
      <line x1="16" y1="16" x2="26" y2="6" stroke="#22d3ee" stroke-width="1.6"/>
      <line x1="16" y1="16" x2="6" y2="26" stroke="#22d3ee" stroke-width="1.6"/>
      <line x1="16" y1="16" x2="26" y2="26" stroke="#22d3ee" stroke-width="1.6"/>
      <circle cx="6" cy="6"  r="2.5" fill="rgba(34,211,238,0.35)" stroke="#22d3ee" stroke-width="1"/>
      <circle cx="26" cy="6" r="2.5" fill="rgba(34,211,238,0.35)" stroke="#22d3ee" stroke-width="1"/>
      <circle cx="6" cy="26" r="2.5" fill="rgba(34,211,238,0.35)" stroke="#22d3ee" stroke-width="1"/>
      <circle cx="26" cy="26" r="2.5" fill="rgba(34,211,238,0.35)" stroke="#22d3ee" stroke-width="1"/>
    </svg>
  `;

  const tag = document.createElement('div');
  tag.className = 'dock-status-badge__tag';
  let countdownEl: HTMLElement | null = null;

  if (b.state === 'pending_approval') {
    tag.classList.add('dock-status-badge__tag--pending');
    tag.innerHTML = `
      <span class="dock-status-badge__dot"></span>
      <span class="dock-status-badge__label">Pending operator approval</span>
    `;
  } else {
    // scheduled
    tag.classList.add('dock-status-badge__tag--scheduled');
    const remaining = (b.scheduledStartAt ?? 0) - Date.now();
    tag.innerHTML = `
      <span class="dock-status-badge__label">Starts in</span>
      <span class="dock-status-badge__countdown">${formatCountdown(remaining)}</span>
    `;
    countdownEl = tag.querySelector('.dock-status-badge__countdown');
  }

  // Mission name caption — small dim text under the tag. Helps disambiguate
  // when multiple docks have pending/scheduled missions on the same map.
  const caption = document.createElement('div');
  caption.className = 'dock-status-badge__caption';
  caption.textContent = b.missionName;

  root.appendChild(icon);
  root.appendChild(tag);
  root.appendChild(caption);

  return { root, countdownEl };
}

export default function DockMissionStatusBadge() {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const missions = useDockDemoStore((s) => s.missions);
  const dockStations = useDockDemoStore((s) => s.dockStations);

  // Active badges keyed by mission id. We diff against the desired set
  // each render and add/remove markers + intervals accordingly.
  const badgesRef = useRef<Map<string, ActiveBadge>>(new Map());

  useEffect(() => {
    if (!map || !isMapReady) return;

    // Compute the desired set: any mission in a state that warrants a
    // map badge (pending or scheduled), with a resolvable bound dock at
    // valid coordinates.
    const desired = new Map<string, BadgeData>();
    for (const m of missions) {
      const state = m.flowState as DemoMissionFlowState | undefined;
      if (state !== 'pending_approval' && state !== 'scheduled') continue;
      if (!m.dockStationId) continue;
      const dock = dockStations.find((d) => d.id === m.dockStationId);
      if (!dock) continue;
      if (dock.lng === 0 && dock.lat === 0) continue;
      desired.set(m.id, {
        missionId: m.id,
        missionName: m.name,
        state,
        lng: dock.lng,
        lat: dock.lat,
        scheduledStartAt: m.scheduledStartAt,
      });
    }

    const cur = badgesRef.current;

    // Remove badges no longer desired.
    for (const [id, badge] of cur) {
      if (!desired.has(id)) {
        if (badge.intervalId !== null) window.clearInterval(badge.intervalId);
        badge.marker.remove();
        cur.delete(id);
      }
    }

    // Add or update remaining badges. We rebuild the element when state
    // transitions (pending → scheduled) so the inner DOM matches; pure
    // position updates are cheap (setLngLat).
    for (const [id, b] of desired) {
      const existing = cur.get(id);
      if (existing) {
        // Position update — covers the dock being moved while a flow
        // is active. The element may also need a rebuild if state
        // changed since last render.
        existing.marker.setLngLat([b.lng, b.lat]);
        const prevStateAttr = existing.marker.getElement().getAttribute('data-state');
        if (prevStateAttr !== b.state) {
          // Rebuild the element to swap pending/scheduled chrome.
          if (existing.intervalId !== null) {
            window.clearInterval(existing.intervalId);
            existing.intervalId = null;
          }
          const { root, countdownEl } = buildBadgeElement(b);
          // mapboxgl.Marker doesn't expose a direct setElement, so we
          // remove + recreate the marker here. Acceptable since state
          // transitions are infrequent (once per flow per mission).
          existing.marker.remove();
          const marker = new mapboxgl.Marker({ element: root, anchor: 'bottom' })
            .setLngLat([b.lng, b.lat])
            .addTo(map);
          existing.marker = marker;
          existing.countdownEl = countdownEl;
        }
        // For scheduled badges, ensure the countdown ticks. The interval
        // is shared per badge; we only re-create it when the badge is
        // (re)created above.
        if (b.state === 'scheduled' && existing.countdownEl && existing.intervalId === null) {
          const cdEl = existing.countdownEl;
          const start = b.scheduledStartAt ?? Date.now();
          const update = () => {
            cdEl.textContent = formatCountdown(start - Date.now());
          };
          update();
          existing.intervalId = window.setInterval(update, 250);
        }
      } else {
        // Fresh badge.
        const { root, countdownEl } = buildBadgeElement(b);
        const marker = new mapboxgl.Marker({ element: root, anchor: 'bottom' })
          .setLngLat([b.lng, b.lat])
          .addTo(map);
        let intervalId: number | null = null;
        if (b.state === 'scheduled' && countdownEl) {
          const start = b.scheduledStartAt ?? Date.now();
          const update = () => {
            countdownEl.textContent = formatCountdown(start - Date.now());
          };
          update();
          intervalId = window.setInterval(update, 250);
        }
        cur.set(id, { marker, countdownEl, intervalId });
      }
    }
  }, [map, isMapReady, missions, dockStations]);

  // Final teardown — remove every marker on unmount so unmounting the
  // editor doesn't leave orphan DOM nodes attached to the map.
  useEffect(() => {
    return () => {
      for (const [, badge] of badgesRef.current) {
        if (badge.intervalId !== null) window.clearInterval(badge.intervalId);
        badge.marker.remove();
      }
      badgesRef.current.clear();
    };
  }, []);

  return null;
}
